import gql from 'graphql-tag';
import { useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { useAccountContext } from 'contexts/account';

export type Notifications =
  GetAccountNotificationsQuery['account']['notifications'];

export const useCurrentPastNotifications = (
  input,
  fetchPolicy: WatchQueryFetchPolicy = 'network-only'
) => {
  const { selectedAccountId } = useAccountContext();
  const { data, error, loading, refetch } = useQuery<
    GetCurrentPastNotificationsQuery,
    GetCurrentPastNotificationsQueryVariables
  >(GET_DASHBOARD_NOTIFICATIONS, {
    variables: {
      id: selectedAccountId,
      input,
    },
    fetchPolicy,
  });

  return {
    data,
    error,
    loading,
    refetch,
  };
};

export const NOTIFICATION_FRAGMENT = gql`
  fragment NotificationData on Notification {
    id
    actionType
    active
    createdAt
    displayValues
    notificationKey
    priorityType
    targetId
    read
    notifiableId
    notifiablePlanId
    utilityAccountDetails {
      accountNumber
      displayServiceAddress
      utilityDisplayName
    }
  }
`;

export const GET_DASHBOARD_NOTIFICATIONS = gql`
  query GetCurrentPastNotifications(
    $id: Int!
    $input: DashboardNotificationsInput
  ) {
    account(id: $id) {
      id
      dashboardNotifications(input: $input) {
        currentNotifications {
          ...NotificationData
        }
        pastNotifications {
          ...NotificationData
        }
        totalCurrentNotifications
        totalPastNotifications
      }
    }
  }
  ${NOTIFICATION_FRAGMENT}
`;

export const currentNotificationsMock = [
  {
    __typename: 'Notification',
    id: 5029496,
    actionType: 'go_to_payment_method',
    active: true,
    createdAt: '2024-11-01T18:11:07.762Z',
    displayValues: {
      title: 'Payment method expired',
      message:
        'Your payment method has expired. Please update your payment details to continue using your Arcadia account.',
      buttonText: 'Add payment method',
    },
    notificationKey: 'seagull_payment_method_expired',
    priorityType: 'high_priority_task_error',
    targetId: null,
    read: true,
    notifiableId: 2923912,
    notifiablePlanId: null,
    utilityAccountDetails: null,
  },
  {
    __typename: 'Notification',
    id: 4889235,
    actionType: 'go_to_payment_method',
    active: true,
    createdAt: '2024-10-01T18:05:09.407Z',
    displayValues: {
      title: 'Payment method expiring soon.',
      message:
        'Your payment method expires in the next 30 days. Please update your payment details to continue using your Arcadia account.',
      buttonText: 'Update payment method',
    },
    notificationKey: 'seagull_payment_method_expiring',
    priorityType: 'high_priority_task_error',
    targetId: null,
    read: true,
    notifiableId: 2923912,
    notifiablePlanId: null,
    utilityAccountDetails: null,
  },
];
const totalCurrentNotificationsMock = 2;

export const pastNotificationsMock = [
  {
    __typename: 'Notification',
    id: 3863369,
    actionType: 'no_action',
    active: true,
    createdAt: '2024-03-18T19:44:51.759Z',
    displayValues: {
      title: 'You’re on the list for a solar farm spot.',
      message:
        'You’ve done your part by signing up for community solar. Now we’re working to find a local solar farm for you. We’ll be in touch soon with more information!',
    },
    notificationKey: 'vnm_status_pending',
    priorityType: 'update',
    targetId: null,
    read: true,
    notifiableId: 2979575,
    notifiablePlanId: 2982730,
    utilityAccountDetails: {
      __typename: 'NotificationUtilityAccountDetails',
      accountNumber: '100108506294',
      displayServiceAddress: '1617 6th Ave, Toms River, NJ 08753',
      utilityDisplayName: 'JCP&L',
    },
  },
  {
    __typename: 'Notification',
    id: 3863368,
    actionType: 'no_action',
    active: true,
    createdAt: '2024-03-18T19:44:51.502Z',
    displayValues: {
      title: 'You’re on the list for a solar farm spot.',
      message:
        'You’ve done your part by signing up for community solar. Now we’re working to find a local solar farm for you. We’ll be in touch soon with more information!',
    },
    notificationKey: 'vnm_status_pending',
    priorityType: 'update',
    targetId: null,
    read: true,
    notifiableId: 2979574,
    notifiablePlanId: 2982729,
    utilityAccountDetails: {
      __typename: 'NotificationUtilityAccountDetails',
      accountNumber: '100108628452',
      displayServiceAddress: '3 Harpers Ferry Rd, Toms River, NJ 08753',
      utilityDisplayName: 'JCP&L',
    },
  },
];
const totalPastNotificationsMock = 2;

export const getCurrentPastNotificationsMock = {
  account: {
    id: 1,
    dashboardNotifications: {
      currentNotifications: currentNotificationsMock,
      pastNotifications: pastNotificationsMock,
      totalCurrentNotifications: totalCurrentNotificationsMock,
      totalPastNotifications: totalPastNotificationsMock,
    },
  },
};

export const useCurrentPastNotificationsMockTest = {
  data: {
    account: {
      id: 1,
      dashboardNotifications: {
        currentNotifications: [...currentNotificationsMock],
        pastNotifications: [...pastNotificationsMock],
        totalCurrentNotifications: 20,
        totalPastNotifications: 30,
      },
    },
  },
  loading: false,
  error: false,
};
